import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import StrategyDrawerHost from "@/components/strategy/StrategyDrawerHost";
import {
  LayoutDashboard,
  Sparkles,
  LineChart,
  Activity,
  AlertTriangle,
  BookOpen,
  Settings,
  LogOut,
  Zap,
  Plug,
  ShieldCheck,
  Radio,
  Brain,
  BarChart2,
  Globe,
  Sun,
  Moon,
  Menu,
  X,
  CalendarDays,
  ListTree,
  Star,
  HelpCircle,
  Newspaper,
  TrendingUp,
  Layers,
  FlaskConical,
  Cpu,
  CandlestickChart,
  ListOrdered,
  Wallet,
  BookMarked,
  Gauge,
  CreditCard,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import LiveTicker from "@/components/LiveTicker";

// section: string = divider label, rest = nav items
const NAV = [
  { to: "/",            label: "Cockpit",        icon: LayoutDashboard, end: true,  testid: "nav-cockpit" },
  { section: "Trading" },
  { to: "/watchlist",   label: "Watchlist",      icon: Star,            testid: "nav-watchlist" },
  { to: "/signals",     label: "Signal Feed",    icon: Radio,           testid: "nav-signals" },
  { to: "/paper",       label: "Orders",         icon: ListOrdered,     testid: "nav-paper",      traderOnly: true },
  { to: "/analytics",   label: "Analytics",      icon: Wallet,          testid: "nav-analytics" },
  { to: "/pnl",         label: "P&L",            icon: ListTree,        testid: "nav-pnl" },
  { to: "/pnl-calendar", label: "P&L Calendar",  icon: CalendarDays,    testid: "nav-pnl-calendar" },
  { section: "Strategies" },
  { to: "/strategies",  label: "Builder",        icon: Layers,          testid: "nav-strategies", traderOnly: true },
  { to: "/backtest",    label: "Backtesting",    icon: FlaskConical,    testid: "nav-backtest",   traderOnly: true },
  { to: "/suite",       label: "Strategy Suites", icon: Gauge,           testid: "nav-suite" },
  { section: "Options" },
  { to: "/orb",         label: "ORB Watchlist",  icon: Zap,             testid: "nav-orb" },
  { to: "/trap",        label: "Trap Detection", icon: AlertTriangle,   testid: "nav-trap" },
  { to: "/market",      label: "OI Analytics",   icon: CandlestickChart,testid: "nav-market" },
  { section: "AI" },
  { to: "/copilot",     label: "Copilot",        icon: Brain,           testid: "nav-copilot" },
  { section: "Research" },
  { to: "/journal",     label: "Journal & Audit",icon: BookMarked,      testid: "nav-journal" },
  { section: "Account" },
  { to: "/brokers",     label: "Brokers",        icon: Plug,            testid: "nav-brokers",    traderOnly: true },
  { to: "/settings",    label: "Settings",       icon: Settings,        testid: "nav-settings" },
  { to: "/admin",       label: "Admin",          icon: ShieldCheck,     testid: "nav-admin",      adminOnly: true },
];

// Bottom nav (mobile) — most accessed items
const BOTTOM_NAV = [
  { to: "/", label: "Cockpit", icon: LayoutDashboard, end: true },
  { to: "/signals", label: "Signals", icon: Radio },
  { to: "/paper", label: "Trade", icon: Activity },
  { to: "/copilot", label: "Copilot", icon: Brain },
  { to: "/settings", label: "Settings", icon: Settings },
];

function useSignalCount() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    fetch("/api/signals?limit=1", { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.total) setCount(d.total); })
      .catch(() => {});
    const id = setInterval(() => {
      fetch("/api/signals?limit=1", { credentials: "include" })
        .then((r) => r.ok ? r.json() : null)
        .then((d) => { if (d) setCount(d.total || 0); })
        .catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);
  return count;
}

function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem("af-theme") || "dark");
  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    document.documentElement.classList.remove("dark", "light");
    document.documentElement.classList.add(next);
    localStorage.setItem("af-theme", next);
    setTheme(next);
  };
  return { theme, toggle };
}

const navLinkClass = ({ isActive }) =>
  `flex items-center gap-2.5 px-3 py-[7px] text-[13px] font-section transition-all duration-150 ${
    isActive
      ? "bg-[var(--accent-muted)] text-[var(--accent)] border-l-[2px] border-[var(--accent)] pl-[10px]"
      : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-2)] border-l-[2px] border-transparent pl-[10px]"
  }`;

function SidebarInner({ user, theme, toggle, logout, navigate, signalCount, setSidebarOpen, filteredNav }) {
  return (
    <>
      {/* Logo */}
      <div className="px-4 py-3.5 flex items-center gap-2.5 border-b border-[var(--border)] shrink-0">
        <button
          data-testid="sidebar-logo"
          onClick={() => { navigate("/"); setSidebarOpen(false); }}
          className="flex items-center gap-2.5 text-left flex-1 min-w-0"
        >
          <div className="w-7 h-7 bg-[var(--accent)] text-white flex items-center justify-center shrink-0 rounded-sm">
            <Zap className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <div className="font-display text-[17px] tracking-tight leading-none text-[var(--text-primary)]">ALGOFORGE</div>
            <div className="text-[10px] text-[var(--text-muted)] tracking-widest uppercase mt-0.5 font-section">AI OS for Traders</div>
          </div>
        </button>
        <button
          className="lg:hidden p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          onClick={(e) => { e.stopPropagation(); setSidebarOpen(false); }}
          aria-label="Close menu"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Nav links */}
      <nav className="flex-1 py-2 px-2 overflow-y-auto min-h-0 space-y-px">
        {filteredNav.map((item) => {
          if (item.section) {
            return (
              <div key={item.section} className="px-3 pt-5 pb-1.5 text-[10px] font-section tracking-[0.14em] uppercase text-[var(--text-muted)]">
                {item.section}
              </div>
            );
          }
          const { to, label, icon: Icon, end, testid } = item;
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              onClick={() => setSidebarOpen(false)}
              className={navLinkClass}
            >
              <Icon className="w-[15px] h-[15px] shrink-0" strokeWidth={1.75} />
              <span className="flex-1 truncate">{label}</span>
              {to === "/signals" && signalCount > 0 && (
                <span className="bg-[var(--accent)] text-white text-[9px] font-bold leading-none px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {signalCount > 99 ? "99+" : signalCount}
                </span>
              )}
            </NavLink>
          );
        })}
        <div className="mt-3 pt-3 border-t border-[var(--border)] flex">
          <NavLink to="/blog" className="flex-1 flex items-center gap-1.5 px-3 py-2 text-[11px] text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors font-section">
            <Newspaper className="w-3.5 h-3.5 shrink-0" /> Blog
          </NavLink>
          <NavLink to="/faq" className="flex-1 flex items-center gap-1.5 px-3 py-2 text-[11px] text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors font-section">
            <HelpCircle className="w-3.5 h-3.5 shrink-0" /> Help
          </NavLink>
        </div>
      </nav>

      {/* Footer — user identity + controls */}
      <div className="border-t border-[var(--border)] shrink-0">
        <div className="px-4 py-2.5 flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-full bg-[var(--bg-surface-2)] flex items-center justify-center shrink-0 text-[10px] font-bold text-[var(--text-secondary)] uppercase">
            {(user?.email || "?").slice(0, 1)}
          </div>
          <div className="min-w-0 flex-1">
            <div data-testid="sidebar-user-email" className="text-[11px] font-mono-data truncate text-[var(--text-primary)] leading-tight">
              {user?.email}
            </div>
            {user?.role && (
              <div className="text-[9px] text-[var(--text-muted)] uppercase tracking-widest mt-0.5">{user.role}</div>
            )}
          </div>
        </div>
        <div className="flex border-t border-[var(--border)]">
          <Button
            data-testid="sidebar-theme-toggle"
            variant="ghost"
            size="sm"
            onClick={toggle}
            className="flex-1 justify-center gap-1.5 rounded-none h-9 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-2)] transition-colors"
          >
            {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            <span className="text-[11px] font-section">{theme === "dark" ? "Light" : "Dark"}</span>
          </Button>
          <div className="w-px bg-[var(--border)]" />
          <Button
            data-testid="sidebar-logout-btn"
            variant="ghost"
            size="sm"
            onClick={logout}
            className="flex-1 justify-center gap-1.5 rounded-none h-9 text-[var(--text-muted)] hover:text-[var(--loss)] hover:bg-[var(--bg-surface-2)] transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="text-[11px] font-section">Logout</span>
          </Button>
        </div>
      </div>
    </>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const signalCount = useSignalCount();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const filteredNav = NAV.filter((n) => {
    if (n.adminOnly && user?.role !== "admin") return false;
    if (n.traderOnly && user?.role === "signal_user") return false;
    return true;
  });

  const sidebarProps = { user, theme, toggle, logout, navigate, signalCount, setSidebarOpen, filteredNav };

  return (
    <div className="h-screen flex overflow-hidden bg-[var(--bg-page)] text-[var(--text-primary)]">
      {/* ── Desktop sidebar (always visible ≥ lg) ─────────────────────────── */}
      <aside className="hidden lg:flex w-56 xl:w-60 shrink-0 border-r border-[var(--border)] flex-col bg-[var(--bg-surface)] h-full">
        <SidebarInner {...sidebarProps} />
      </aside>

      {/* ── Mobile sidebar overlay (< lg) ─────────────────────────────────── */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <aside className="w-64 max-w-[80vw] bg-[var(--bg-surface)] border-r border-[var(--border)] flex flex-col h-full overflow-hidden shadow-2xl">
            <SidebarInner {...sidebarProps} />
          </aside>
          {/* Backdrop */}
          <div
            className="flex-1 bg-black/60 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
        </div>
      )}

      {/* ── Main content ──────────────────────────────────────────────────── */}
      <main className="flex-1 min-w-0 flex flex-col h-full overflow-hidden">
        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center gap-3 px-3 py-3 border-b border-[var(--border)] bg-[var(--bg-surface)] shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 txt-secondary hover:text-[var(--text-primary)] transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2"
          >
            <div className="w-6 h-6 bg-[var(--accent)] text-white flex items-center justify-center">
              <Zap className="w-3.5 h-3.5" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg tracking-tight text-[var(--text-primary)]">ALGOFORGE</span>
          </button>
          {signalCount > 0 && (
            <NavLink to="/signals" className="ml-auto">
              <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {signalCount > 99 ? "99+" : signalCount}
              </span>
            </NavLink>
          )}
          <button
            onClick={toggle}
            className="p-1.5 txt-secondary hover:text-[var(--text-primary)] transition-colors ml-auto"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>

        <LiveTicker />

        <div className="flex-1 overflow-y-auto pb-16 lg:pb-0">{children}</div>
        {/* RU-STRATDRILL SD-1: the one shared strategy drill-down drawer */}
        <StrategyDrawerHost />

        {/* ── Mobile bottom nav ─────────────────────────────────────────── */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[var(--bg-surface)] border-t border-[var(--border)] flex items-center">
          {BOTTOM_NAV.filter((n) => {
            if (n.to === "/paper" && user?.role === "signal_user") return false;
            return true;
          }).map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] font-section transition-colors relative ${
                  isActive ? "text-[var(--text-primary)]" : "txt-muted"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className={`w-5 h-5 ${isActive ? "text-[var(--accent)]" : ""}`} strokeWidth={isActive ? 2.5 : 1.5} />
                  <span>{label}</span>
                  {to === "/signals" && signalCount > 0 && (
                    <span className="absolute top-1.5 right-[calc(50%-18px)] bg-red-500 text-white text-[9px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center">
                      {signalCount > 9 ? "9+" : signalCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
          {/* More → opens sidebar */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] font-section txt-muted"
          >
            <Menu className="w-5 h-5" strokeWidth={1.5} />
            <span>More</span>
          </button>
        </nav>
      </main>
    </div>
  );
}
